import React, { useState, useRef, useContext, useEffect } from 'react';
import {
	DEFAULT_YEARS_TO_RETIREMENT_UNKNOWN,
	yearsToRetirementFor
} from '../../utils/monthlyProjection';
import {
	Loader2,
	RefreshCw,
	SlidersHorizontal,
	Receipt,
	ClipboardList,
	HelpCircle,
	FileText,
	Footprints
} from 'lucide-react';
import { ArcaContext } from '../../context/ArcaContext.context';
import {
	ArcaPlan,
	ARCA_PLANS,
	getAvailablePlans,
	getAvailablePlansByUserType,
	findArcaPlanById,
	findArcaPlanByName
} from '../../constants/arcaPlans';
import ChosenPlanCard from './ChosenPlanCard';
import SectionHeading from './SectionHeading';
import SituationAnalysis from './SituationAnalysis';
import ReportSimulator from './ReportSimulator';
import TaxSavingsCard from './TaxSavingsCard';
import { ReportSettings } from './ReportSettingsCard';
import TrustFAQ from './TrustFAQ';
import { TRANSFER_FAQ_ITEMS, NEW_PLAN_FAQ_ITEMS } from './trustFAQContent';
import PlanDocumentsSection from './PlanDocumentsSection';
import OtherPlansSheet from './OtherPlansSheet';
import ExplorationProjectionV2 from './ExplorationProjectionV2';
import NewPlanSimulator from './NewPlanSimulator';
import NewPlanAnalysis from './NewPlanAnalysis';
import { getArcaPlansFinancials } from '../../api/pension-plan-catalog/getArcaPlansFinancials';
import { PensionPlanTransfer } from '../../api/pension-plan-transfers/interfaces/PensionPlanTransfer';
import { TransferComparisonData, getTransferComparison } from '../../api/pension-plan-transfers';
import updateMyTransfer from '../../api/pension-plan-transfers/updateMyTransfer';
import useArcaPlanReturns from '../../hooks/useArcaPlanReturns';
import useTransferReturns from '../../hooks/useTransferReturns';
import { B2cLandingScenario } from '../../types/individualDashboard';
import { abrirPestanaPendiente, PestanaPendiente } from '../../movil/pestanaPendiente';
import { hasArcaReturns } from '../../utils/transferReturns';
import { originPlanCode } from '../../utils/originPlanCode';

interface ExplorationViewProps {
	transfer: PensionPlanTransfer;
	scenario: B2cLandingScenario;
	translate: (key: string) => string;
	userName?: string;
	/** Edad del usuario (del onboarding). Horizonte de proyección: 67 − edad. */
	currentAge?: number;
	comparisonData?: TransferComparisonData | null;
	isFreelance?: boolean;
	/**
	 * Al confirmar el plan, el padre bloquea el plan y arranca el KYC. Devuelve la URL del KYC
	 * (o null si no arrancó) para redirigir la pestaña que abrimos en el click.
	 */
	onProcessStarted?: () => Promise<string | null | void> | void;
	/** Plan individual en proceso: las otras peticiones vivas del funnel (ver `OtherPlansSheet`). */
	siblingTransferIds?: string[];
	/** Tras guardar otro plan: el padre refresca lo que depende de él (selector, tarjeta). */
	onPlanChanged?: (updated: PensionPlanTransfer) => void;
}

/**
 * Timeline "Qué pasa cuando confirmes" (Sección 8 de la spec). Reemplaza la card
 * genérica "¿Y ahora qué?". Copy y tiempos difieren entre traspaso y nuevo plan.
 */
const TransferNextSteps: React.FC<{
	isNoPlan: boolean;
	translate: (key: string) => string;
}> = ({ isNoPlan, translate }) => {
	const steps = [
		{
			time: translate('individual.exploration.nextSteps.step1.time') || '2 minutos',
			title:
				translate('individual.exploration.nextSteps.step1.title') || 'Verificación de identidad',
			desc:
				translate('individual.exploration.nextSteps.step1.body') ||
				'Te pediremos tu DNI/NIE y haremos una verificación rápida online. Sin papel.'
		},
		{
			time: translate('individual.exploration.nextSteps.step2.time') || '1-3 días',
			title: translate('individual.exploration.nextSteps.step2.title') || 'Trámite con gestoras',
			desc:
				translate('individual.exploration.nextSteps.step2.body') ||
				'Enviamos la solicitud. No tienes que hacer nada.'
		},
		{
			time: isNoPlan
				? translate('individual.exploration.nextSteps.step3.time.noPlan') || '1-2 días'
				: translate('individual.exploration.nextSteps.step3.time.transfer') || '4-6 semanas',
			title: isNoPlan
				? translate('individual.exploration.nextSteps.step3.title.noPlan') ||
					'Tu plan queda abierto'
				: translate('individual.exploration.nextSteps.step3.title.transfer') ||
					'Tu dinero llega a tu nuevo plan',
			desc: isNoPlan
				? translate('individual.exploration.nextSteps.step3.body.noPlan') ||
					'Alta con la gestora confirmada. Primera aportación según el día que elijas.'
				: translate('individual.exploration.nextSteps.step3.body.transfer') ||
					'El traspaso se ejecuta directamente entre planes. Te avisamos.'
		}
	];

	return (
		<div className="space-y-3">
			<ol className="grid list-none gap-3 sm:grid-cols-2 lg:grid-cols-3">
				{steps.map(({ time, title, desc }, i) => (
					<li key={i} className="surface-card p-4 space-y-1">
						<p className="text-xs font-semibold text-primary">{time}</p>
						<p className="text-sm font-medium leading-snug">{title}</p>
						<p className="text-xs text-muted-foreground leading-relaxed">{desc}</p>
					</li>
				))}
			</ol>
			<p className="text-xs text-center text-muted-foreground">
				{isNoPlan
					? translate('individual.exploration.nextSteps.footer.noPlan') ||
						'Sin comisión de apertura · Sin permanencia mínima'
					: translate('individual.exploration.nextSteps.footer.transfer') ||
						'Sin coste · Sin tributación · Sin papeleo de tu parte'}
			</p>
		</div>
	);
};

const ExplorationView: React.FC<ExplorationViewProps> = ({
	transfer,
	scenario,
	translate,
	userName,
	currentAge: knownAge,
	comparisonData,
	isFreelance,
	onProcessStarted,
	siblingTransferIds,
	onPlanChanged
}) => {
	const { token } = useContext(ArcaContext);
	// Sin su edad, 30 años solo para el horizonte: nunca para pedir tramos de ciclo de vida.
	const currentAge = knownAge ?? 30;
	const [currentTransfer, setCurrentTransfer] = useState(transfer);
	const [otherPlansOpen, setOtherPlansOpen] = useState(false);
	const [processStarted, setProcessStarted] = useState(false);
	// Pestaña que abrimos (en blanco) de forma síncrona en el click para arrancar el KYC. La
	// redirigimos a la URL real del KYC cuando el backend responde (evita el bloqueador de pop-ups,
	// que solo respeta window.open dentro del propio gesto de click).
	const kycWindowRef = useRef<PestanaPendiente | null>(null);
	const [allPlans, setAllPlans] = useState<ArcaPlan[]>(ARCA_PLANS);
	// Comparación como estado local (init de la prop): al cambiar de plan la re-pedimos para que
	// la gráfica y la tabla usen las rentabilidades del NUEVO plan Arca, no las del original.
	const [comparison, setComparison] = useState(comparisonData);
	const isNoPlan = scenario === B2cLandingScenario.NO_PLAN;
	// Aportación mensual que el usuario eligió en el onboarding (la manda comparsa en
	// metadata). Se usa como default en AMBOS flujos. Si no llega: en plan nuevo cae a 30 €
	// (mínimo del onboarding, ya significativo; el slider baja hasta 10 €), y en traspaso a
	// 0 € (puede tener saldo sin aportar). Sacar el default fuera de este useState rompía el
	// prellenado de la aportación en el traspaso (salía siempre 0).
	// Máximo mensual deducible por tipo de usuario (individual 125 €/1.500 € año; autónomo
	// 479 €/5.750 € año). Capa el default para que la aportación nunca supere el deducible.
	const maxMonthlyContribution = isFreelance ? 479 : 125;
	const onboardingMonthly = Number(currentTransfer.metadata?.monthly_contribution);
	const defaultMonthly =
		onboardingMonthly > 0 ? Math.min(onboardingMonthly, maxMonthlyContribution) : isNoPlan ? 30 : 0;
	// Años hasta la jubilación: el onboarding pregunta directamente los años (no la edad)
	// y comparsa los manda en metadata. Los priorizamos; si no llegan, los derivamos de la
	// edad (67 − edad), que en fase de informe puede no estar (birthdate se rellena en KYC).
	const onboardingYears = Number(currentTransfer.metadata?.years_to_retirement);
	// Quien viene del corte del funnel (solo ese camino manda `saldo_rango`) crea la cuenta sin
	// años ni edad: para él, 17 años (product#76). El resto, sin años ni edad, sigue con 67 − 30.
	const yearsUnknownFromCut =
		!(onboardingYears > 0) &&
		knownAge === undefined &&
		Boolean(currentTransfer.metadata?.saldo_rango);
	// Años enteros: la proyección mes a mes va por años completos.
	const initialYears = Math.round(
		onboardingYears > 0
			? onboardingYears
			: yearsUnknownFromCut
				? DEFAULT_YEARS_TO_RETIREMENT_UNKNOWN
				: 67 - currentAge
	);
	// Ajustes editables del informe. Traspaso: años, saldo, aportación actual y %.
	// Plan nuevo: solo años y aportación (arranca desde 0), con una aportación por
	// defecto >0 porque en un plan nuevo la aportación es el motor de la proyección.
	const [reportSettings, setReportSettings] = useState<ReportSettings>({
		// Años hasta jubilación, acotado al rango del slider [1, 47].
		years: Math.min(47, Math.max(1, initialYears)),
		balance: parseFloat(currentTransfer.amount || '0'),
		monthlyContribution: defaultMonthly,
		transferPct: 100
	});
	const updateReportSettings = (patch: Partial<ReportSettings>) =>
		setReportSettings((s) => ({ ...s, ...patch }));

	useEffect(() => {
		if (!token) return;
		getArcaPlansFinancials(token)
			.then(setAllPlans)
			.catch(() => {});
	}, [token]);

	// Rentabilidad de cada plan de Arca (plan nuevo, lista de planes, tarjeta del plan), de
	// comparsa (`/plan-returns/arca-plans`, una vez por sesión). `undefined` mientras llega; si
	// falla, mapa vacío: todos «sin datos», nunca la tasa de ARCA_PLANS. Con su edad, los de
	// ciclo de vida llegan con sus tramos (chirigota#592): con los años que dio en el funnel (los
	// de la curva) o, si no, los de su edad.
	// Del corte y sin años ni edad, los mismos 17 años del slider (los del análisis del funnel).
	const retirementYears =
		yearsToRetirementFor(currentTransfer.metadata?.years_to_retirement, knownAge) ??
		(yearsUnknownFromCut ? DEFAULT_YEARS_TO_RETIREMENT_UNKNOWN : undefined);
	const arcaPlanReturns = useArcaPlanReturns(token, retirementYears);
	// El simulador de «tu edad» deja elegir otra: con los tramos de la real no cuadraría.
	const arcaPlanReturnsSinEdad = useArcaPlanReturns(token);

	useEffect(() => {
		if (!processStarted) return;
		// El padre bloquea el plan y arranca el KYC; con su URL redirigimos la pestaña ya abierta.
		Promise.resolve(onProcessStarted?.()).then((kycUrl) => {
			const pestana = kycWindowRef.current;
			if (!pestana) return;
			if (kycUrl) {
				pestana.irA(kycUrl);
			} else {
				// Sin URL (KYC no arrancó o el usuario cae al paso KYC de la plataforma): no dejamos
				// una pestaña en blanco colgando.
				pestana.cerrar();
			}
			kycWindowRef.current = null;
		});
	}, [processStarted, onProcessStarted]);

	// Sincroniza la comparación cuando el padre la actualiza (carga inicial async).
	useEffect(() => {
		setComparison(comparisonData);
	}, [comparisonData]);

	// Al cambiar de plan (OtherPlansSheet actualiza currentTransfer vía updateMyTransfer, que ya
	// persiste el nuevo selectedArcaPlanId), re-pedimos la comparación: el endpoint deriva el plan
	// Arca del transfer, así que devolverá las rentabilidades del plan elegido. Saltamos el primer
	// render porque la comparación inicial ya llega por prop.
	const skipFirstComparisonFetch = useRef(true);
	useEffect(() => {
		if (skipFirstComparisonFetch.current) {
			skipFirstComparisonFetch.current = false;
			return;
		}
		if (!token || !currentTransfer.id) return;
		// Guardamos contra respuestas obsoletas: si el usuario encadena cambios de plan
		// (A→B→C), cada effect anterior se cancela, así solo la respuesta del plan vigente
		// aplica (evita que una respuesta lenta de B pise a la de C).
		let cancelled = false;
		getTransferComparison(token, currentTransfer.id)
			.then((res) => {
				if (!cancelled && res.success && res.data) setComparison(res.data);
			})
			.catch(() => {});
		return () => {
			cancelled = true;
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [currentTransfer.selectedArcaPlanId]);

	const plansFilteredByType =
		isFreelance !== undefined ? getAvailablePlansByUserType(isFreelance, allPlans) : allPlans;
	const visiblePlans = getAvailablePlans(currentTransfer.origin, plansFilteredByType);

	const selectedPlanId =
		currentTransfer.selectedArcaPlanId ||
		(currentTransfer.metadata?.selectedPlanId as string | undefined);
	const resolvedPlan =
		(selectedPlanId ? findArcaPlanById(selectedPlanId, visiblePlans) : undefined) ??
		findArcaPlanByName(currentTransfer.metadata?.plan_name ?? '', visiblePlans);
	const projectionPlans = resolvedPlan
		? visiblePlans.filter((p) => p.id === resolvedPlan.id)
		: visiblePlans;

	// Plan del bloque de comparación (Path T): se deriva del propio `comparison` (no de
	// currentTransfer) para que nombre y rentabilidades vayan SIEMPRE sincronizados. Al cambiar
	// de plan, hasta que no llega la nueva comparación se sigue viendo el plan anterior completo,
	// y luego cambian nombre y cifras a la vez — nunca nombre nuevo con cifras viejas.
	const comparisonPlan =
		(comparison ? findArcaPlanById(comparison.arcaPlan.id, visiblePlans) : undefined) ??
		resolvedPlan;

	// Rentabilidades del traspaso: las decide comparsa (`/plan-returns`, guía v3.1) para el plan
	// de Arca de la comparación frente al del usuario. `undefined` mientras llegan; `null` si
	// fallan: entonces no se pinta ninguna cifra (nunca una tasa inventada).
	const arcaSlug = comparisonPlan?.catalogSlug;
	// Código del plan del usuario solo si es del registro (DGSFP o PPA): hay peticiones con «» o
	// «SIN-CODIGO», y comparsa rechazaría la llamada entera. Sin él, el plan del usuario sale sin datos.
	const originDgsfp = originPlanCode(currentTransfer.DGSFP_code);
	// Sin plan de origen (plan nuevo) no hay traspaso que comparar: no se pide nada.
	const planReturns = useTransferReturns(
		token,
		isNoPlan ? undefined : arcaSlug,
		originDgsfp,
		retirementYears
	);
	const transferReturns = hasArcaReturns(planReturns) ? planReturns : null;
	// Solo se espera si hay algo que pedir: sin plan de Arca resuelto no hay petición.
	const planReturnsLoading = !!arcaSlug && planReturns === undefined;

	const name = userName || '';

	const greeting = (
		isNoPlan
			? translate('individual.exploration.greeting.noPlan')
			: scenario === B2cLandingScenario.PLAN_DETECTIVE
				? translate('individual.exploration.greeting.detective')
				: translate('individual.exploration.greeting.located')
	).replace('{name}', name);

	const subtitle = (
		isNoPlan
			? translate('individual.exploration.subtitle.noPlan')
			: scenario === B2cLandingScenario.PLAN_DETECTIVE
				? translate('individual.exploration.subtitle.detective')
				: translate('individual.exploration.subtitle.located')
	).replace('{name}', name);

	const planManagerName = resolvedPlan?.manager || resolvedPlan?.name || 'tu gestora';

	// Comparativa "plan actual vs elección" (Path T con datos). Fuera de esto (Path N o
	// traspaso sin comparativa) se muestra la card de elección en solitario.
	const transferBalance = parseFloat(currentTransfer.amount || '0');
	const showComparison =
		!isNoPlan && !!comparison && transferBalance > 0 && (planReturnsLoading || !!transferReturns);

	const ctaLabel = isNoPlan
		? (translate('individual.exploration.cta.openPlan') || 'Abrir mi plan con {manager}').replace(
				'{manager}',
				planManagerName
			)
		: translate('individual.exploration.cta.transfer') || 'Elegir plan e iniciar verificación';

	const handleCtaClick = () => {
		// Abrir la pestaña del KYC AQUÍ (síncrono, dentro del gesto de click) para que el navegador
		// no la bloquee. Aún no tenemos la URL; la redirigimos en el efecto cuando el backend responda.
		// Dentro de la app móvil no se abre nada todavía: ver `abrirPestanaPendiente`.
		kycWindowRef.current = abrirPestanaPendiente();
		// Guardar en metadata la elección del usuario (fire & forget, no bloquea el flujo):
		// aportación mensual en Path N, importe/porcentaje a traspasar en Path T.
		const metadataUpdates: Record<string, number> = {};
		if (isNoPlan) {
			metadataUpdates.monthlyContribution = reportSettings.monthlyContribution;
		} else {
			metadataUpdates.selectedTransferAmount =
				(reportSettings.balance * reportSettings.transferPct) / 100;
			metadataUpdates.transferPct = reportSettings.transferPct;
		}
		if (token && currentTransfer.id && Object.keys(metadataUpdates).length > 0) {
			updateMyTransfer(token, currentTransfer.id, {
				metadata: {
					...currentTransfer.metadata,
					...metadataUpdates
				}
			}).catch(() => {});
		}
		setProcessStarted(true);
	};

	if (processStarted) {
		return (
			<div className="individual-v2 -mx-4 -mt-4 min-h-screen flex items-center justify-center">
				<Loader2 className="w-8 h-8 animate-spin text-primary" />
			</div>
		);
	}

	return (
		<div className="individual-v2 -mx-4 -mt-4 min-h-screen">
			<div className="max-w-2xl lg:max-w-5xl mx-auto px-4 pt-8 pb-32 space-y-6">
				{/* Hero */}
				<div className="animate-fade-up space-y-1">
					{/* 🔴 `font-heading` Y `tracking-[-0.03em]` explícitos. Hasta el 29/09/26 los dos
					    se los daba la regla `.individual-v2 h1` de `index.css`, que forzaba Space
					    Grotesk, `font-weight: 700` y ese tracking a todos los `h1` del ámbito. Esa
					    regla se retiró porque le ganaba a las clases de cualquier componente.
					    Este es el único `h1` del repo que dependía de ella, así que pide las tres
					    cosas por su cuenta. El tracking se olvidó en el primer intento: medido,
					    el saludo salía 7 px (3%) más ancho y visiblemente más suelto. */}
					<h1 className="text-2xl font-bold tracking-[-0.03em] font-heading">{greeting}</h1>
					<p className="text-muted-foreground text-sm leading-relaxed">{subtitle}</p>
				</div>

				{/* Ajusta tu informe — controles primero: ajustes (izq) + gráfica con selector (der).
				    Todo lo que depende de estos ajustes va debajo. Path T, ancho completo. */}
				{!isNoPlan && comparison && comparisonPlan && transferBalance > 0 && planReturnsLoading && (
					<div data-testid="plan-returns-loading" className="flex justify-center py-12">
						<Loader2 className="w-6 h-6 animate-spin text-primary" />
					</div>
				)}

				{!isNoPlan && comparison && comparisonPlan && transferBalance > 0 && transferReturns && (
					<div className="animate-fade-up space-y-3" style={{ animationDelay: '0.1s' }}>
						<SectionHeading
							icon={SlidersHorizontal}
							title={(
								translate('individual.section.configure') ||
								'Configura tu plan actual y compáralo con {plan}'
							).replace('{plan}', comparisonPlan.name)}
							action={
								<button
									onClick={() => setOtherPlansOpen(true)}
									className="shrink-0 flex items-center gap-1.5 rounded-full border border-primary/30 px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/5"
								>
									<RefreshCw className="w-4 h-4" />
									{translate('individual.comparison.changePlan') || 'Cambiar plan'}
								</button>
							}
						/>
						<ReportSimulator
							comparisonData={comparison}
							resolvedPlan={comparisonPlan}
							returns={transferReturns}
							settings={reportSettings}
							onChange={updateReportSettings}
							maxMonthly={maxMonthlyContribution}
							translate={translate}
						/>
					</div>
				)}

				{/* Ahorro fiscal (IRPF) — franja salarial + resultado. No depende de las rentabilidades. */}
				{!isNoPlan && comparison && comparisonPlan && transferBalance > 0 && (
					<div className="animate-fade-up space-y-3" style={{ animationDelay: '0.2s' }}>
						<SectionHeading
							icon={Receipt}
							title={
								translate('individual.section.tax') ||
								'¿Cómo ayuda tu plan a pagar menos impuestos?'
							}
						/>
						<TaxSavingsCard
							annualContribution={reportSettings.monthlyContribution * 12}
							maxAnnualContribution={maxMonthlyContribution * 12}
							initialIncome={Number(currentTransfer.metadata?.income) || undefined}
							translate={translate}
						/>
					</div>
				)}

				{/* Resumen del análisis: consejo + tabla de comparación en una sola card.
				    Debajo de los ajustes: se recalcula al tocarlos. Path T con datos. */}
				{!isNoPlan && comparison && comparisonPlan && transferBalance > 0 && transferReturns && (
					<div className="animate-fade-up space-y-3" style={{ animationDelay: '0.25s' }}>
						<SectionHeading
							icon={ClipboardList}
							title={translate('individual.analysis.summaryTitle') || 'Resumen de tu análisis'}
						/>
						<SituationAnalysis
							comparisonData={comparison}
							resolvedPlan={comparisonPlan}
							returns={transferReturns}
							balance={reportSettings.balance}
							translate={translate}
						/>
					</div>
				)}

				{/* ===== Plan nuevo (sin origen): mismo informe, sin comparativa ===== */}
				{isNoPlan && resolvedPlan && (
					<>
						{/* Simulador: ajustes (años + aportación) + proyección de una sola línea */}
						<div className="animate-fade-up space-y-3" style={{ animationDelay: '0.1s' }}>
							<SectionHeading
								icon={SlidersHorizontal}
								title={(
									translate('individual.section.simulateNewPlan') || 'Simula tu plan {plan}'
								).replace('{plan}', resolvedPlan.name)}
								action={
									<button
										onClick={() => setOtherPlansOpen(true)}
										className="shrink-0 flex items-center gap-1.5 rounded-full border border-primary/30 px-4 py-2 text-sm font-medium text-primary transition-colors hover:bg-primary/5"
									>
										<RefreshCw className="w-4 h-4" />
										{translate('individual.comparison.changePlan') || 'Cambiar plan'}
									</button>
								}
							/>
							{arcaPlanReturns === undefined ? (
								<div data-testid="arca-returns-loading" className="flex justify-center py-12">
									<Loader2 className="w-6 h-6 animate-spin text-primary" />
								</div>
							) : (
								<NewPlanSimulator
									resolvedPlan={resolvedPlan}
									planReturn={arcaPlanReturns[resolvedPlan.catalogSlug] ?? null}
									settings={reportSettings}
									onChange={updateReportSettings}
									maxMonthly={maxMonthlyContribution}
									translate={translate}
								/>
							)}
						</div>

						{/* Ahorro fiscal (IRPF) — usa la aportación mensual elegida */}
						<div className="animate-fade-up space-y-3" style={{ animationDelay: '0.2s' }}>
							<SectionHeading
								icon={Receipt}
								title={
									translate('individual.section.tax') ||
									'¿Cómo ayuda tu plan a pagar menos impuestos?'
								}
							/>
							<TaxSavingsCard
								annualContribution={reportSettings.monthlyContribution * 12}
								maxAnnualContribution={maxMonthlyContribution * 12}
								initialIncome={Number(currentTransfer.metadata?.income) || undefined}
								translate={translate}
							/>
						</div>

						{/* Tu plan elegido — tabla de datos del plan + consejo, sin comparación */}
						<div className="animate-fade-up space-y-3" style={{ animationDelay: '0.25s' }}>
							<SectionHeading
								icon={ClipboardList}
								title={translate('individual.section.chosenPlan') || 'Tu plan elegido'}
							/>
							{arcaPlanReturns === undefined ? (
								<div
									data-testid="arca-returns-loading-analysis"
									className="flex justify-center py-6"
								>
									<Loader2 className="w-6 h-6 animate-spin text-primary" />
								</div>
							) : (
								<NewPlanAnalysis
									resolvedPlan={resolvedPlan}
									planReturn={arcaPlanReturns[resolvedPlan.catalogSlug] ?? null}
									translate={translate}
								/>
							)}
						</div>
					</>
				)}

				{/* Resto de secciones — ancho de lectura cómodo en desktop */}
				<div className="lg:max-w-2xl space-y-6">
					{/* Traspaso sin comparativa: card de elección en solitario */}
					{!isNoPlan && !showComparison && (
						<div className="animate-fade-up" style={{ animationDelay: '0.2s' }}>
							<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
								{translate('individual.exploration.chosenPlan.title') || 'Tu elección'}
							</p>
							<ChosenPlanCard
								transfer={currentTransfer}
								plans={allPlans}
								planReturns={arcaPlanReturns}
								translate={translate}
								onViewOthers={() => setOtherPlansOpen(true)}
							/>
						</div>
					)}

					{/* Plan nuevo sin plan resuelto: simulador genérico (fallback) */}
					{isNoPlan && !resolvedPlan && (
						<div className="animate-fade-up" style={{ animationDelay: '0.25s' }}>
							<p className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-2">
								{translate('individual.projection.title') || 'Tu simulación'}
							</p>
							<div className="surface-card p-5">
								<ExplorationProjectionV2
									translate={translate}
									plans={projectionPlans}
									planReturns={arcaPlanReturnsSinEdad}
								/>
							</div>
						</div>
					)}
				</div>

				{/* Preguntas de confianza — ancho completo en desktop */}
				<div className="animate-fade-up space-y-3" style={{ animationDelay: '0.35s' }}>
					<SectionHeading
						icon={HelpCircle}
						title={translate('individual.exploration.faq.title') || 'Preguntas antes de empezar'}
					/>
					<TrustFAQ
						items={isNoPlan ? NEW_PLAN_FAQ_ITEMS : TRANSFER_FAQ_ITEMS}
						defaultOpenIndex={0}
						onCtaClick={() => window.dispatchEvent(new CustomEvent('arca:open-assistant'))}
					/>
				</div>

				{/* Documentación del plan — solo si hay plan resuelto */}
				{resolvedPlan && (
					<div className="animate-fade-up space-y-3" style={{ animationDelay: '0.375s' }}>
						<SectionHeading
							icon={FileText}
							title={translate('individual.planDocs.title') || 'Documentación del plan'}
						/>
						<PlanDocumentsSection plan={resolvedPlan} translate={translate} />
					</div>
				)}

				{/* Próximos pasos — pegado al CTA para reforzar causa-efecto */}
				<div className="animate-fade-up space-y-3" style={{ animationDelay: '0.4s' }}>
					<SectionHeading
						icon={Footprints}
						title={translate('individual.section.nextSteps') || 'Próximos pasos'}
					/>
					<TransferNextSteps isNoPlan={isNoPlan} translate={translate} />
				</div>
			</div>

			{/* Sticky CTA */}
			<div className="sticky-cta">
				<div>
					<button
						onClick={handleCtaClick}
						className="w-full bg-gradient-brand text-white font-semibold rounded-full py-3.5 text-base transition-opacity hover:opacity-90 active:opacity-80"
					>
						{ctaLabel}
					</button>
				</div>
			</div>

			{/* Sheets */}
			<OtherPlansSheet
				open={otherPlansOpen}
				transfer={currentTransfer}
				token={token}
				plans={visiblePlans}
				yearsToRetirement={retirementYears}
				translate={translate}
				onClose={() => setOtherPlansOpen(false)}
				onPlanChanged={(updated) => {
					setCurrentTransfer(updated);
					onPlanChanged?.(updated);
				}}
				siblingTransferIds={siblingTransferIds}
			/>
		</div>
	);
};

export default ExplorationView;
